import {
  IBookingUpdateModuleType,
  IBookingShiftAppointmentData,
  IUserBookingsListData,
} from "@/api_framework/api_modals/Bookings";
import { ICBLListData } from "@/api_framework/api_modals/CBL";
import { IPatientWaitingListData } from "@/api_framework/api_modals/Patients";
import ModalDoubleAction from "@/app/components/ui/Modals/DoubleAction";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useBookingRescheduleAndCancelAPI } from "@/app/hooks/api_hooks/bookings/useBookingRescheduleAndCancelationAPI";
import { useShiftBookingListAPI } from "@/app/hooks/api_hooks/bookings/useShiftBookingListAPI";
import { useUserBookingsListAPI } from "@/app/hooks/api_hooks/bookings/useUserBookingsListApi";
import useReFetchCBL from "@/app/hooks/api_hooks/process/useReFetchCBL";
import useRefetchAtHomeListing from "@/app/hooks/api_hooks/process/useReFetchAtHomeListing";
import useRefetchAtHomeStats from "@/app/hooks/api_hooks/process/useReFetchAtHomeStats";
import useRefetchPWL from "@/app/hooks/api_hooks/process/useReFetchPWL";
import useRefetchPWLStats from "@/app/hooks/api_hooks/process/useReFetchPWLStats";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { BsExclamationCircle } from "react-icons/bs";
import { ICancelationModalProps } from "../interface";
import useFormValidations from "@/app/hooks/useFormValidations";
import Input from "@/app/components/ui/Inputs/Input";

import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useIsBookingChargableIsCancel } from "@/app/hooks/api_hooks/bookings/useIsBookingChargableIsCancel";
import { useNavigate } from "react-router";

interface IBookingCommentsFormFields {
  cancelReason: string;
  otherReason?: string;
}

export interface IBookingCancelationModalState {
  bookingDetails?: IPatientWaitingListData;
  CBLBookingDetails?: ICBLListData;
  appointmentShiftDetails?: IBookingShiftAppointmentData;
  customerProfileBookingDetails?: IUserBookingsListData;
  from?: "CUSTOMER_PROFILE" | "PATIENT_WAITING_LIST" | "APPOINTMENT_SHIFT" | "ROTA" | "CBL" | "AT_HOME_SERVICES";
}

const CancelationModal: React.FC<ICancelationModalProps> = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate()
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { appointmentFilter } = routeState.state as ICustomerProfileState;
  /* Instatiations */
  const { appointmentCancelationReasons } = useArrayConstants();
  const { setInvisible } = useModalActions("APPOINTMENT_CANCELATION");
  const modalState = useModalState("APPOINTMENT_CANCELATION");
  const { forAllAcceptWithOutStartWithDotAndSpace } = useFormValidations();

  const formHook = useForm<IBookingCommentsFormFields>({
    mode: "onChange",
    defaultValues: {},
  });
  /* Instatiations Ends */
  const [otherCancilation, setOtherCancilation] = useState(false);

  /* API Starts */
  const { updatedBookingId, execute: cancelBooking } = useBookingRescheduleAndCancelAPI();
  const fetchCustomerBookingsAPI = useUserBookingsListAPI();
  const refetchPWL = useRefetchPWL();
  const refetchCBL = useReFetchCBL();
  const refetchPWLStats = useRefetchPWLStats();
  const refetchAtHomeListing = useRefetchAtHomeListing();
  const refetchAtHomeStats = useRefetchAtHomeStats();
  const fetchShiftAppointment = useShiftBookingListAPI();
  const { execute: fetchBookingStatus, bookingDetails: cancilationDetails } = useIsBookingChargableIsCancel();
  const [status, setStatus] = useState<boolean>(false);
  /* API Ends */

  /* Redux States */
  const {
    bookingDetails,
    from,
    CBLBookingDetails,
    customerProfileBookingDetails: CPDetails,
    appointmentShiftDetails: ASDetails,
  } = modalState as IBookingCancelationModalState;
  /* Redux States Ends */

  /* Handlers / Actions Starts */
  const resolveBookingId = (): string | null => {
    if (from === "PATIENT_WAITING_LIST" || from === "AT_HOME_SERVICES") return bookingDetails?.booking_id ?? null;
    if (from === "CBL") return CBLBookingDetails?.booking_id ?? null;
    if (from === "APPOINTMENT_SHIFT" || from === "ROTA") return ASDetails?.booking_id ?? null;
    if (from === "CUSTOMER_PROFILE") return CPDetails?.booking_id ?? null;
    return null;
  };

  const resolveCancelReason = () =>
    otherCancilation ? formHook?.getValues("otherReason") : formHook?.getValues("cancelReason");

  const resolveSourceModule = (): IBookingUpdateModuleType => {
    if (from === "PATIENT_WAITING_LIST" || from === "AT_HOME_SERVICES" || from === "CBL") return "pwl_module";
    if (from === "CUSTOMER_PROFILE") return "profile_module";
    if (from === "ROTA") return "rota_module";
    return "appointment_module";
  };

  const handleCancelation = () => {
    formHook?.trigger().then((isValid) => {
      if (isValid) {
        const bookingId = resolveBookingId();
        const cancelReason = resolveCancelReason();
        if (!bookingId || !cancelReason) return;

        cancelBooking(
          {
            booking_id: bookingId,
            cancel_message: cancelReason,
            source_module: resolveSourceModule(),
          },
          bookingId,
        ).then(() => {
          formHook.setValue("cancelReason", null);
          formHook.setValue("otherReason", null);
          if (from === "PATIENT_WAITING_LIST") {
            refetchPWLStats().then(() => {
              refetchPWL();
            });
            return;
          }
          if (from === "AT_HOME_SERVICES") {
            refetchAtHomeStats().then(() => {
              refetchAtHomeListing();
            });
            return;
          }
          if (from === "CBL") {
            refetchCBL();
            return;
          }
          if (from === "ROTA") {
            navigate(`/app/appointment/shift-appointment?t=${Date.now()}`);
            return;
          }
          fetchCustomerBookingsAPI(CPDetails?.user_id, CPDetails?.pet_id, {
           append: false,
            filter_by: appointmentFilter.toLowerCase(),
            limit: 10,
            page: 1,
          });
        });
      }
    });
  };

  useEffect(() => {
    const cancilation = formHook.watch("cancelReason");
    if (cancilation === "Other") {
      setOtherCancilation(true);
    } else {
      setOtherCancilation(false);
    }
  }, [formHook.watch("cancelReason")]);
  /* Handlers / Actions Ends */

  useAfterFirstRender(() => {
    if (updatedBookingId) {
      setInvisible();
    }
  }, [updatedBookingId]);

  const handleFetchBookingStatus = async () => {
    let bookingId = null;

    if (from === "PATIENT_WAITING_LIST" || from === "AT_HOME_SERVICES") {
      bookingId = bookingDetails?.booking_id;
    }
    if (from === "CBL") {
      bookingId = CBLBookingDetails?.booking_id;
    }
    if (from === "APPOINTMENT_SHIFT") {
      bookingId = ASDetails?.booking_id;
    }
    if (from === "CUSTOMER_PROFILE") {
      bookingId = CPDetails?.booking_id;
    }

    if (from === "ROTA") {
      bookingId = ASDetails?.booking_id;
    }
    fetchBookingStatus(bookingId).then(({ status }) => {
      if (status) {
        setStatus(true);
        //
      } else {
        setStatus(false);
      }
    });
  };

  return (
    <ModalDoubleAction
      showIcon={false}
      contentOverflow={true}
      modalId="APPOINTMENT_CANCELATION"
      onVisibleCallback={handleFetchBookingStatus}
      button={{
        primaryButtonText: "Cancel Appointment",
        primaryButtonClassname: "bg-red-500 text-white",
        secondaryButtonText: "Go Back",
        primaryCallback: () => {
          handleCancelation();
        },
        secondaryCallback: () => {
          formHook.setValue("cancelReason", null);
          setInvisible();
        },
        shouldHideOnClick: false,
      }}
      content={
        status ? (
          <div>
            <p className="text-sm">Your appointment will be cancelled please select a appropriate reason for it.</p>
            {cancilationDetails?.payment_status?.toLowerCase() === "paid" &&
              (cancilationDetails?.is_refundable ? (
                <p className="text-left font-medium text-green-500  text-sm  mt-2 flex items-center gap-1">
                  <BsExclamationCircle className="w-4 h-4 text-green-500 text-sm " />
                  Advance payment of Rs. {cancilationDetails?.amount} will be refunded to the customer
                </p>
              ) : (
                <p className="text-left font-medium text-red-500  text-sm  mt-2 flex items-center gap-1">
                  <BsExclamationCircle className="w-4 h-4 text-red-500 text-sm " />
                  Cancellation charges of Rs. {cancilationDetails?.amount} will be applied. No refund for the customer.
                </p>
              ))}
            {/* {bookingDetails?.payment_details?.payment_status &&
            (bookingDetails?.payment_details?.is_refundable ? (
              <p className="text-left font-medium text-green-500  text-sm  mt-2 flex items-center gap-1">
                <BsExclamationCircle className="w-4 h-4 text-green-500 text-sm " />
                Advance payment of Rs. {bookingDetails?.payment_details?.amount} will be refunded to the customer
              </p>
            ) : (
              <p className="text-left font-medium text-red-500  text-sm  mt-2 flex items-center gap-1">
                <BsExclamationCircle className="w-4 h-4 text-red-500 text-sm " />
                Cancellation charges of Rs. {bookingDetails?.payment_details?.amount} will be applied. No refund for the
                customer.
              </p>
            ))}
          {CPDetails?.payment_details?.payment_status &&
            (CPDetails?.payment_details?.is_refundable ? (
              <p className="text-left font-medium text-green-500  text-sm  mt-2 flex items-center gap-1">
                <BsExclamationCircle className="w-4 h-4 text-green-500 text-sm " />
                Advance payment of Rs. {CPDetails?.payment_details?.amount} will be refunded to the customer
              </p>
            ) : (
              <p className="text-left font-medium text-red-500  text-sm  mt-2 flex items-center gap-1">
                <BsExclamationCircle className="w-4 h-4 text-red-500 text-sm " />
                Cancellation charges of Rs. {CPDetails?.payment_details?.amount} will be applied. No refund for the
                customer.
              </p>
            ))} */}

            <p className="text-left font-semibold text-gray-700 mt-4">
            Cancellation Reasons <sup className="text-red-600">*</sup>{" "}
            </p>
            <div className="mt-2 w-2/4 mb-8">
              <SearchableSelectMenu
                errorMessages={[{ message: "Cancel Reason Required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("cancelReason", item.title);
                  }
                }}
                fieldError={formHook.formState.errors.cancelReason}
                register={formHook.register("cancelReason", { required: true })}
                selectItems={appointmentCancelationReasons}
                placeholder="Select Cancel Reason"
                showTooltips={false}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={
                  appointmentCancelationReasons?.filter((oc) => oc.title === formHook.watch("cancelReason"))[0]
                }
                listBoxClassName="w-full"
                className="text-gray-800 "
                containerClassName="w-full"
              />
            </div>
            {otherCancilation && (
              <Input
                className="text-xs w-1/2"
                placeholder="please enter the reason"
                register={formHook.register("otherReason", {
                  required: true,
                  ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                })}
                fieldError={formHook.formState.errors.otherReason}
                errorMessages={[
                  { message: "Other reason is required", type: "required" },
                  forAllAcceptWithOutStartWithDotAndSpace.errors,
                ]}
              />
            )}
          </div>
        ) : (
          <TableSkeleton />
        )
      }
      title="Appointment Cancellation"
      titleIcon="GrAndroid"
      titleIconClassName="text-green-500"
    />
  );
};

export default CancelationModal;
